import * as React from 'react';
import { usePublicClient, useAccount, useWalletClient } from 'wagmi';
import { ethers } from 'ethers';
import { type HttpTransport } from 'viem';
import {
    DefaultChainId,
    NETWORK_CONFIG,
    RPC,
    SupportChainIDs,
} from '@/constant';
import Web3 from 'web3';

export function publicClientToProvider(publicClient: any, walletClient?: any) {
    const network = {
        chainId: publicClient?.chain.id || DefaultChainId,
        name: publicClient?.chain.name || NETWORK_CONFIG[DefaultChainId].name,
        ensAddress: publicClient?.chain.contracts?.ensRegistry?.address,
    };

    const currentProviderChainId =
        Number(
            (window as any).web3 &&
                (window as any).web3.currentProvider?.chainId,
        ) || 1;

    if (walletClient) {
        const web3: any = new Web3(walletClient as any);
        web3.eth.extend({
            methods: [
                {
                    name: 'chainId',
                    call: 'eth_chainId',
                    outputFormatter: web3.utils.hexToNumber,
                },
            ],
        });

        if (web3?.currentProvider) {
            const provider = new ethers.BrowserProvider(web3?.currentProvider);
            return provider;
        }
    }

    if (
        SupportChainIDs.indexOf(currentProviderChainId) >= 0 &&
        (window as any).web3.currentProvider?.selectedAddress
    ) {
        return new ethers.BrowserProvider((window as any).web3.currentProvider);
    }
    if (publicClient?.transport.type === 'fallback') {
        return new ethers.FallbackProvider(
            (
                publicClient?.transport
                    .transports as ReturnType<HttpTransport>[]
            ).map(
                ({ value }) => new ethers.JsonRpcProvider(value?.url, network),
            ),
        );
    }
    return new ethers.JsonRpcProvider(
        publicClient?.transport.url || RPC[DefaultChainId],
        network,
    );
}

/** Hook to convert a viem Public Client to an ethers.js Provider. */
export function useEthersProvider({
    chainId,
    account,
}: { chainId?: number; account?: string } = {}) {
    const publicClient = usePublicClient({ chainId });
    const { chainId: accountChainId } = useAccount();
    const { address } = useAccount();
    const { data: walletClient } = useWalletClient();
    return React.useMemo(
        () =>
            publicClientToProvider(
                publicClient,
                accountChainId === chainId && address
                    ? walletClient
                    : undefined,
            ),
        [publicClient, account, walletClient, address],
    );
}
